import { useCallback, useEffect, useMemo, useState } from 'react';
import { iotApi } from '../lib/iotApi';
import { indexCustomNames, type SectorDisplayNameRow } from '../soverdi-clone/adapters/sectorNaming';

const bindingNameCode = (bindingId: string): string => `binding:${bindingId}`;

/** Farm-scoped display metadata only; no equipment or command requests. */
export function useSectorDisplayNames(farmId: string) {
  const [snapshot, setSnapshot] = useState<{ farmId: string; names: Map<string, string> }>({ farmId: '', names: new Map() });
  const customNames = useMemo(() => snapshot.farmId === farmId ? snapshot.names : new Map<string, string>(), [snapshot, farmId]);
  const [namesUnavailable, setNamesUnavailable] = useState(false);
  const [namesEpoch, setNamesEpoch] = useState(0);

  useEffect(() => {
    if (!farmId) return undefined;
    let mounted = true;
    iotApi
      .getJson<SectorDisplayNameRow[]>(
        `/iot/sector-display-names?farm_id=${encodeURIComponent(farmId)}`,
      )
      .then((rows) => {
        if (!mounted) return;
        setSnapshot({ farmId, names: indexCustomNames(Array.isArray(rows) ? rows : []) });
        setNamesUnavailable(!Array.isArray(rows));
      })
      .catch(() => {
        if (!mounted) return;
        setSnapshot({ farmId, names: new Map() });
        setNamesUnavailable(true);
      });
    return () => {
      mounted = false;
    };
  }, [farmId, namesEpoch]);

  const renameSector = useCallback(
    async (siteName: string, sectorCode: string, name: string | null, bindingId?: string): Promise<void> => {
      if (!farmId) throw new Error('farm context unavailable');
      await iotApi.put('/iot/sector-display-names', {
        farm_id: farmId,
        site_name: siteName,
        sector_code: bindingId ? bindingNameCode(bindingId) : sectorCode,
        custom_display_name: name,
      });
      setNamesEpoch((value) => value + 1);
    },
    [farmId],
  );

  return { customNames, renameSector, namesUnavailable };
}
